import React, { useState, useEffect, Fragment, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { toast } from "react-toastify"
import { useTranslation } from "react-i18next"
import clsx from "clsx"

import {
  clearCart,
  validateCartContents,
  printEditedOrder
} from "utils"
import {
  Spinner,
  FoodSidebarEditOrderModal,
  CartStockValidationResultModal
} from "components"
import {
  changeDeliveryType,
  editOfflineOrder,
  hideFoodSidebar
} from "@redux"
import { editOrder } from "services"
import classes from "./FoodSidebarCheckoutAndPlaceOrderButtons.module.scss"

const FoodSidebarCheckoutAndPlaceOrderButtons = () => {
  const [t, i18n] = useTranslation()

  const history = useHistory()

  const dispatch = useDispatch()

  const orderForEditDetails =
    useSelector(state => state.orderForEditDetails)

  const order =
    useSelector(state => state.order)

  const { deliveryType, deliveryAddress } =
    useSelector(state => state.deliveryInfo)

  const cashierBranchId =
    useSelector(state => state.userAccountInfo.branchId)

  const isOnline =
    useSelector(state => !state.internetStatus.forceOffline) &&
    !orderForEditDetails?.offlineOrderId

  const [sending, setSending] = useState(false)
  const [openEditOrderModal, setOpenEditOrderModal] = useState(false)
  const [problematicItems, setProblematicItems] = useState([])
  const [openCartValidationResultModal, setOpenCartValidationResultModal] = useState(false)

  const singleTableOrderDetails = useRef(null)

  const validateCart = async () => {
    const previousOrder =
      orderForEditDetails !== null
        ?
        {
          items: orderForEditDetails.items,
          combos: orderForEditDetails.combos
        }
        : null

    const problematicItems =
      await validateCartContents(
        order.items,
        order.combos,
        previousOrder
      )

    if (problematicItems.length === 0) return true

    setProblematicItems(problematicItems)

    setOpenCartValidationResultModal(true)

    setSending(false)

    return false
  }

  const getRequestBody = () => {
    const orderItems =
      order.items.map(item => ({
        asComboSet: false,
        optionId: item.optionId,
        foodId: item.foodId,
        quantity: item.quantity,
        systemDiscount: item.systemDiscount * item.quantity,
        specificationItemIds:
          item.specificationItems.map(si => ({
            _id: si._id,
            quantity: si.quantity,
            builderStepKey: si.builderStepKey,
            portion: si.portion
          }))
      }))

    const comboItems = []
    order.combos.forEach((combo, cIndex) =>
      combo.items.forEach(comboItem =>
        comboItems.push({
          foodId: comboItem.foodId,
          optionId: comboItem.optionId,
          quantity: comboItem.quantity,
          specificationItemIds: [],
          comboSet: {
            code: combo.code,
            comboSetItemId: comboItem.comboSetItemId,
            groupNumber: cIndex + 1
          }
        })
      )
    )

    const couponCodes = []

    order.combos.forEach(combo =>
      couponCodes.push(combo.code)
    )

    const items = [...orderItems, ...comboItems]

    const data = {
      items,
      branchId: cashierBranchId,
      deliveryType,
      isCod: true,
      isPrinted: true,
      couponCodes,
      tableNumber: order.tableNumber
    }

    if (deliveryType === "delivery") {
      data["address"] = deliveryAddress
    }

    return data
  }

  const validate = () => {
    const isCartEmpty =
      order.items.length === 0 &&
      order.combos.length === 0

    if (isCartEmpty) {
      toast.error(t("لطفا_یک_کالا_انتخاب_کنید"), { autoClose: false })

      return false
    }

    return true
  }

  const handleEditOrderOffline = () => {
    if (!validate()) return

    try {
      dispatch(
        editOfflineOrder(
          orderForEditDetails.offlineOrderId,
          { order }
        )
      )

      clearCart()

      toast.success(t("سفارش_ویرایش_شد"))
    } catch (err) {
      console.log(err)

      setSending(false)

      toast.error(t("سفارش_ویرایش_نشد"))
    }
  }

  const handleEditOrder = async () => {
    if (!validate()) return

    setSending(true)

    try {
      const result = await validateCart()

      if (!result) return

      const requestBody = getRequestBody()

      const query =
        orderForEditDetails.isGuestOrder
          ? { orderId: orderForEditDetails.orderId }
          : { accountId: orderForEditDetails.selectedUserAccount?._id }

      const response =
        await editOrder(
          orderForEditDetails.orderId,
          requestBody,
          query
        )

      printEditedOrder(response)

      setSending(false)

      clearCart()

      toast.success(t("سفارش_ویرایش_شد"))
    } catch (err) {
      console.log(err)

      setSending(false)

      toast.error(t("سفارش_ویرایش_نشد"))
    }
  }

  const handleNavigateToCheckout = (mode) => {
    const isCartEmpty =
      order.items.length === 0 &&
      order.combos.length === 0

    if (isCartEmpty) {
      toast.error(t("لطفا_یک_کالا_انتخاب_کنید"), { autoClose: false })

      return
    }

    dispatch(
      changeDeliveryType("dineIn")
    )

    dispatch(
      hideFoodSidebar()
    )

    history.push("/checkout", { mode })
  }

  const clearCartWhenEditingOfflineOrderInOnlineMode = () => {
    if (!isOnline) return

    if (!orderForEditDetails?.offlineOrderId) return

    clearCart()
  }

  useEffect(() => {
    singleTableOrderDetails.current = null
  }, [order.tableNumber])

  useEffect(() => {
    clearCartWhenEditingOfflineOrderInOnlineMode()
  }, [isOnline])

  const editMode =
    orderForEditDetails !== null

  const placeOrderAndCheckoutMode = !editMode

  const isLtr = i18n.dir(i18n.language) === "ltr"

  return (
    <Fragment>
      <div className={classes["row"]}>
        {
          editMode &&
          <button
            onClick={isOnline ? handleEditOrder : handleEditOrderOffline}
            disabled={sending}
            className={classes["button"]}
          >
            {!sending ? t("ویرایش_سفارش") : <Spinner color="white" size={16} />}
          </button>
        }

        {
          placeOrderAndCheckoutMode &&
          <Fragment>
            <button
              disabled={sending}
              onClick={() => handleNavigateToCheckout("placeOrder")}
              className={classes["button"]}
            >
              {t("ایجاد_سفارش")}
            </button>

            <button
              disabled={sending}
              onClick={() => handleNavigateToCheckout("checkout")}
              className={clsx(classes["checkout"], isLtr && classes["checkout-ltr"])}
            >
              {t("پرداخت")}
            </button>
          </Fragment>
        }
      </div>

      <CartStockValidationResultModal
        problematicItems={problematicItems}
        openCartValidationResultModal={openCartValidationResultModal}
        setOpenCartValidationResultModal={setOpenCartValidationResultModal}
      />
      <FoodSidebarEditOrderModal
        tableNumber={order.tableNumber}
        singleTableOrderDetails={singleTableOrderDetails}
        openEditOrderModal={openEditOrderModal}
        setOpenEditOrderModal={setOpenEditOrderModal}
      />
    </Fragment>
  )
}

export default React.memo(FoodSidebarCheckoutAndPlaceOrderButtons)
